import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { analyticsService } from 'src/services/analytics/analyticsService';
import { RFQ_BUILDER_CONVERSATION_ERROR } from 'src/services/analytics/rfqBuilderEvents';
import createSSEConnection from 'src/shared/config/sseApiService';
import { UploadedFile } from 'src/shared/types/uploadedFile';
import { SSE } from 'sse.js';

export interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  files?: UploadedFile[];
}

interface RfqConversationContextValue {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  isLoading: boolean;
  isSending: boolean;
  setIsSending: React.Dispatch<React.SetStateAction<boolean>>;
  connectToConversation: (
    messageToSend: string,
    fileIds?: string[],
    shouldUpdateParams?: boolean
  ) => Promise<void>;
  addUserMessage: (text: string, files?: UploadedFile[]) => void;
  resetConversation: () => void;
}

const RfqConversationContext =
  createContext<RfqConversationContextValue | null>(null);

interface RfqConversationProviderProps {
  children: ReactNode;
  rfqId: number | undefined;
}

export function RfqConversationProvider({
  children,
  rfqId
}: RfqConversationProviderProps): JSX.Element {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [, setSearchParams] = useSearchParams();

  const sseRef = useRef<SSE | null>(null);
  const currentAiMessageIdRef = useRef<string | null>(null);

  // Cleanup SSE connection on unmount
  useEffect(() => {
    return () => {
      if (sseRef.current) {
        sseRef.current.close();
      }
    };
  }, []);

  const connectToConversation = useCallback(
    async (
      messageToSend: string,
      fileIds: string[] = [],
      shouldUpdateParams: boolean = false
    ): Promise<void> => {
      if (!rfqId || (!messageToSend.trim() && fileIds.length === 0)) {
        return Promise.resolve();
      }

      // Clean up any existing SSE connection
      if (sseRef.current) {
        sseRef.current.close();
      }

      setIsLoading(true);
      currentAiMessageIdRef.current = null;

      return new Promise<void>((resolve, reject) => {
        const payload: { message: string; file_ids?: string[] } = {
          message: messageToSend
        };

        if (fileIds.length > 0) {
          payload.file_ids = fileIds;
        }

        const source = createSSEConnection({
          endpoint: `/rfq/${rfqId}/conversation/`,
          payload,
          onMessage: (data) => {
            if (!currentAiMessageIdRef.current) {
              // Create new AI message
              const newMessageId = Date.now().toString();

              currentAiMessageIdRef.current = newMessageId;
              const newMessage: Message = {
                id: newMessageId,
                text: data,
                isUser: false,
                timestamp: new Date()
              };

              setMessages((prev) => [...prev, newMessage]);
            } else {
              // Append to existing message
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === currentAiMessageIdRef.current
                    ? { ...msg, text: msg.text + data }
                    : msg
                )
              );
            }
          },
          onError: (event) => {
            console.error('RFQ conversation SSE error', event);
            analyticsService.trackEvent(RFQ_BUILDER_CONVERSATION_ERROR, {
              rfq_id: rfqId
            });
            const errorMessage: Message = {
              id: Date.now().toString(),
              text: 'Sorry, I encountered an error connecting to the conversation service.',
              isUser: false,
              timestamp: new Date()
            };

            setMessages((prev) => [...prev, errorMessage]);
            setIsLoading(false);
            setIsSending(false);
            currentAiMessageIdRef.current = null;
            reject(new Error('SSE connection error'));
          },
          onClose: () => {
            setIsLoading(false);
            setIsSending(false);
            currentAiMessageIdRef.current = null;

            if (shouldUpdateParams) {
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);

                newParams.set('file-status', 'success');
                newParams.delete('files');

                return newParams;
              });
            }

            resolve();
          }
        });

        sseRef.current = source;
        source.stream();
      });
    },
    [rfqId, setSearchParams]
  );

  const addUserMessage = useCallback((text: string, files?: UploadedFile[]) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      text,
      isUser: true,
      timestamp: new Date(),
      files
    };

    setMessages((prev) => [...prev, userMessage]);
  }, []);

  const resetConversation = useCallback(() => {
    setMessages([]);
    setIsLoading(false);
    setIsSending(false);
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
  }, []);

  const value: RfqConversationContextValue = {
    messages,
    setMessages,
    isLoading,
    isSending,
    setIsSending,
    connectToConversation,
    addUserMessage,
    resetConversation
  };

  return (
    <RfqConversationContext.Provider value={value}>
      {children}
    </RfqConversationContext.Provider>
  );
}

export function useRfqConversationContext(): RfqConversationContextValue {
  const context = useContext(RfqConversationContext);

  if (!context) {
    throw new Error(
      'useRfqConversationContext must be used within a RfqConversationProvider'
    );
  }

  return context;
}
